import { CompensationReviewCompensationItem, type Prisma } from "@prisma/client";
import { type AsyncReturnType } from "type-fest";
import { type AppContext } from "~/lib/context";
import { getRequiredUser } from "~/lib/getRequiredUser";
import { DEFAULT_MAX_REVIEWERS_COUNT } from "~/services/compensation-review/campaigns/admin/createCampaign";
import { externalEmployeeSelectForAutocomplete } from "~/services/external-employee/autocompleteListCompanyExternalEmployees";

const select = {
  id: true,
  companyId: true,
  maxReviewersCount: true,
  allowReviewersToSetCurrentReviewer: true,
  finalReviewer: {
    select: {
      id: true,
      externalEmployee: { select: externalEmployeeSelectForAutocomplete },
    },
  },
  company: {
    select: {
      defaultSalaryGrid: {
        select: {
          tiersMode: true,
          tiersNames: true,
        },
      },
    },
  },
  admins: { select: { userId: true } },
  reviewerGuidelines: true,
  additionalFields: {
    select: {
      id: true,
      name: true,
      nature: true,
    },
    orderBy: {
      name: "asc",
    },
  },
  budgets: {
    select: {
      id: true,
      budgetingMethod: true,
      prorationStartDate: true,
      name: true,
      amount: true,
      compensationItems: true,
      isPromotionBudget: true,
      isLockedBudget: true,
      relativeVariablePayEnabled: true,
      budgetDefinitionMethod: true,
      salaryMassMeasure: true,
      recommendationDefinitionMethod: true,
      distributionKey: true,
      adjustmentCriteria: true,
      marketDataPositioningMeasure: true,
      recommendationsAllocation: true,
      skippedPerformanceRatingIds: true,
      skippedPositionings: true,
      subBudgets: {
        select: {
          id: true,
          name: true,
          amount: true,
          adjustmentCriteria: true,
          marketDataPositioningMeasure: true,
          recommendationsAllocation: true,
          performanceRewardFactor: true,
          adjustmentFactor: true,
          postSmoothingBudgetDifference: true,
          postSmoothingOverriddenEmployeesCount: true,
          skippedPerformanceRatingIds: true,
          skippedPositionings: true,
        },
        orderBy: { name: "asc" },
      },
      increaseRules: {
        select: {
          id: true,
          position: true,
          target: true,
          type: true,
          unit: true,
          amount: true,
          compensationItem: true,
          hardLimit: true,
          populationId: true,
          population: {
            select: {
              id: true,
              name: true,
            },
          },
        },
        orderBy: { position: "asc" },
      },
    },
    orderBy: { id: "asc" },
  },
} satisfies Prisma.OffCycleReviewConfigurationSelect;

export const fetchOffCycleReviewConfiguration = async (ctx: AppContext) => {
  const user = getRequiredUser(ctx);

  const offCycleReviewConfiguration = await ctx.prisma.offCycleReviewConfiguration.findFirst({
    where: { companyId: user.companyId },
    select,
  });

  if (!offCycleReviewConfiguration) {
    return ctx.prisma.offCycleReviewConfiguration.create({
      data: {
        company: {
          connect: { id: user.companyId },
        },
        maxReviewersCount: DEFAULT_MAX_REVIEWERS_COUNT,
        allowReviewersToSetCurrentReviewer: true,
        budgets: {
          create: {
            name: "Budget",
            compensationItems: Object.values(CompensationReviewCompensationItem),
            isLockedBudget: false,
            isPromotionBudget: false,
            relativeVariablePayEnabled: false,
          },
        },
      },
      select,
    });
  }

  return offCycleReviewConfiguration;
};

export type FetchOffCycleReviewConfigurationResult = AsyncReturnType<typeof fetchOffCycleReviewConfiguration>;
